import React,{useState,useEffect,useMemo} from "react";
import { makeStyles } from 'tss-react/mui';
import { Button, TextField, Chip } from "@mui/material";
import Autocomplete from '@mui/material/Autocomplete';
import ForwardIcon from "@mui/icons-material/ShortcutOutlined";
import { setError } from "../store/features/error/errorSlice";
import { connect } from "react-redux";
import AdminDialog from "./adminDialog";
import AttachmentRow from "./attachmentRow";
import ContactAvatar from "./ContactAvatar";

const mapDispatchToProps = {
  setError,
};

const useStyles = makeStyles()((theme) => ({
  field: {
    "& .MuiInputBase-input::placeholder": {
      fontSize: "0.85em",
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  },
  // A colleague in the list: avatar, name, extension.
  option: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  optionName: {
    fontSize: 14.5,
    lineHeight: 1.35,
    color: theme.palette.text.primary,
  },
  optionDetail: {
    fontSize: 12.5,
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
  },
  chip: {
    "&.MuiChip-root": {
      height: 26,
      fontSize: 13,
      color: theme.palette.primary.main,
      backgroundColor: "rgba(1, 186, 239, 0.13)",
    },
    "& .MuiChip-deleteIcon": {
      fontSize: 17,
      color: "inherit",
      opacity: 0.6,
      "&:hover": {
        color: "inherit",
        opacity: 1,
      },
    },
  },
  textButton: {
    textTransform: "none",
    color: theme.palette.text.secondary,
  },
  primary: {
    textTransform: "none",
    minWidth: 112,
    color: "#fff",
    backgroundColor: theme.palette.primary.light,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      filter: "brightness(0.93)",
      boxShadow: "none",
    },
  },
}));

const nameOf = (person) => `${person.firstName || ""} ${person.lastName || ""}`.trim();
const extensionOf = (person) => String(person.preferredDeviceNumbers?.office || person.voicemail?.mailbox || "");

const Forward = (props) => {
    const { classes, cx } = useStyles();
    const { open, setOpen, value, setError, type, phonebook, forwardFunction, idKey, groupKey, defaultValues } = props;
    const [ recipient, setRecipient ] = useState([]);
    const [ sending, setSending ] = useState(false);

    // Colleagues who have an extension to forward to, by name.
    const options = useMemo(
      () => (phonebook || [])
        .filter((person) => extensionOf(person) !== "")
        .sort((a, b) => nameOf(a).localeCompare(nameOf(b))),
      [phonebook]
    );

    // Every time it opens, it starts without recipients.
    useEffect(() => {
      if (open) {
        setRecipient([]);
        setSending(false);
      }
    }, [open, value]);

    const kind = String(type || "").toLowerCase();
    const canForward = recipient.length > 0 && !!value && !sending;

    const handleForward = async()=>{
      if (!canForward) return;
      setSending(true);
      // A copy: defaultValues is shared with the rest of the list.
      const queue = {
        ...defaultValues,
        [kind]: recipient.map((item) => ({
          username: item.preferredDeviceNumbers.office,
        })),
      };

      try{
        let resp = groupKey
        ? await forwardFunction(queue,value[idKey],value[groupKey])
        : await forwardFunction(queue,value[idKey])

        if (resp instanceof Error) throw resp.message;

        setError({
          visible: true,
          type: "success",
          message: `${type} successfully forwarded.`,
        });
        setOpen(false)
      } catch(error){
        setSending(false);
        setError({
          visible: true,
          type: "error",
          message: `Error: ${error}`,
        });
      }
    }

    if (!open) return null;

    return (
      <AdminDialog
        open={open}
        onClose={() => setOpen(false)}
        icon={<ForwardIcon />}
        title={`Forward ${kind}`}
        subtitle="Choose the colleagues to forward it to."
        maxWidth={520}
        footer={
          <>
            <Button onClick={() => setOpen(false)} className={classes.textButton}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!canForward}
              className={classes.primary}
              onClick={handleForward}
            >
              {sending ? "Forwarding…" : "Forward"}
            </Button>
          </>
        }
      >
        <AttachmentRow file={value} type={type} />
        <div>
          <Autocomplete
            multiple
            size="small"
            id="forward-recipients"
            options={options}
            value={recipient}
            onChange={(event, next) => setRecipient(next)}
            // A colleague already chosen is not offered again.
            filterSelectedOptions
            disableCloseOnSelect
            getOptionLabel={(option) => `${nameOf(option)} ${extensionOf(option)}`.trim()}
            isOptionEqualToValue={(option, chosen) => extensionOf(option) === extensionOf(chosen)}
            noOptionsText="No colleague matches."
            renderOption={(optionProps, option) => {
              const { key, ...rest } = optionProps;
              return (
                <li key={key} {...rest}>
                  <span className={classes.option}>
                    <ContactAvatar name={nameOf(option)} number={extensionOf(option)} image={option.profileImage} size={32} />
                    <span>
                      <div className={classes.optionName}>{nameOf(option) || extensionOf(option)}</div>
                      <div className={classes.optionDetail}>Ext. {extensionOf(option)}</div>
                    </span>
                  </span>
                </li>
              );
            }}
            renderValue={(people, getItemProps) => people.map((person, index) => {
              // React wants the key passed directly, not inside the spread.
              const { key, ...itemProps } = getItemProps({ index });
              return (
                <Chip
                  key={key}
                  label={nameOf(person) || extensionOf(person)}
                  size="small"
                  {...itemProps}
                  // After the spread, which carries a class of its own.
                  className={cx(itemProps.className, classes.chip)}
                />
              );
            })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="To"
                placeholder={recipient.length === 0 ? "Name or extension" : ""}
                variant="outlined"
                fullWidth
                autoFocus
                className={classes.field}
                slotProps={{ ...params.slotProps, inputLabel: { ...(params.slotProps && params.slotProps.inputLabel), shrink: true } }}
              />
            )}
          />
        </div>
      </AdminDialog>
    );
}

const mapStateToProps = (state) => {
  return {
    phonebook: state.phonebook,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Forward);
